/**
 * Field controls of a task (US 4.4, 4.10, 4.11, 4.14): the description with
 * bold / italics / bullets / links, labels, repeat, reminder and the
 * Squad's custom fields.
 */
import { Fragment, useEffect, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Bell, BellOff, Bold, Check, Italic, Link2, List, Repeat, Tag, X } from "lucide-react";
import { cn } from "@weco/ui";
import { Dropdown } from "../ui";
import { taskApi, useTasksSettings, type FieldType, type Label, type RepeatRule, type SquadField } from "./api";
import { DatePickerPanel } from "./DatePicker";
import { addDays, iso, parseIso, previewDates, zonedInstant } from "./dates";
import { Modal, CloseButton } from "../hubs/parts";

// ── Description: a tiny safe Markdown (US 4.4) ────────────────────────────

const SAFE_URL = /^(https?:\/\/|mailto:)/i;

function inline(text: string, key: string): ReactNode[] {
  // **bold**, _italic_ / *italic*, [label](url), bare URLs — nothing else is
  // ever turned into markup, so no HTML from a description reaches the page.
  const out: ReactNode[] = [];
  const re = /(\*\*[^*]+\*\*)|(_[^_]+_|\*[^*]+\*)|(\[[^\]]+\]\([^)\s]+\))|(https?:\/\/[^\s)]+)/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let i = 0;
  while ((m = re.exec(text))) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const k = `${key}-${i++}`;
    if (m[1]) out.push(<strong key={k}>{m[1].slice(2, -2)}</strong>);
    else if (m[2]) out.push(<em key={k}>{m[2].slice(1, -1)}</em>);
    else if (m[3]) {
      const [, label, url] = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(m[3]) ?? [];
      out.push(
        SAFE_URL.test(url) ? (
          <a key={k} href={url} target="_blank" rel="noreferrer noopener" className="text-primary underline underline-offset-2">
            {label}
          </a>
        ) : (
          label
        ),
      );
    } else if (m[4]) {
      out.push(
        <a key={k} href={m[4]} target="_blank" rel="noreferrer noopener" className="text-primary underline underline-offset-2 break-all">
          {m[4]}
        </a>,
      );
    }
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

export function Markdown({ text, className }: { text: string; className?: string }) {
  const blocks: ReactNode[] = [];
  let bullets: string[] = [];
  const flush = () => {
    if (bullets.length) {
      blocks.push(
        <ul key={`ul${blocks.length}`} className="list-disc ps-5 space-y-0.5">
          {bullets.map((b, i) => (
            <li key={i}>{inline(b, `li${blocks.length}-${i}`)}</li>
          ))}
        </ul>,
      );
      bullets = [];
    }
  };
  text.split("\n").forEach((line, i) => {
    const bullet = /^\s*[-*•]\s+(.*)$/.exec(line);
    if (bullet) {
      bullets.push(bullet[1]);
      return;
    }
    flush();
    blocks.push(line.trim() ? <p key={`p${i}`}>{inline(line, `p${i}`)}</p> : <div key={`b${i}`} className="h-2" />);
  });
  flush();
  return <div className={cn("space-y-1 leading-relaxed break-words", className)}>{blocks}</div>;
}

export function DescriptionEditor({
  value,
  disabled,
  onSave,
}: {
  value: string;
  disabled?: boolean;
  onSave: (value: string) => void;
}) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const ref = useRef<HTMLTextAreaElement>(null);
  useEffect(() => setDraft(value), [value]);
  const wrap = (before: string, after = before, placeholder = "") => {
    const el = ref.current;
    if (!el) return;
    const { selectionStart: a, selectionEnd: b } = el;
    const picked = draft.slice(a, b) || placeholder;
    const next = draft.slice(0, a) + before + picked + after + draft.slice(b);
    setDraft(next);
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(a + before.length, a + before.length + picked.length);
    });
  };
  const bullet = () => {
    const el = ref.current;
    if (!el) return;
    const start = draft.lastIndexOf("\n", el.selectionStart - 1) + 1;
    setDraft(draft.slice(0, start) + "- " + draft.slice(start));
    requestAnimationFrame(() => el.focus());
  };
  const commit = () => {
    setEditing(false);
    if (draft !== value) onSave(draft);
  };
  if (!editing) {
    return (
      <div
        role={disabled ? undefined : "button"}
        tabIndex={disabled ? undefined : 0}
        onClick={() => !disabled && setEditing(true)}
        onKeyDown={(e) => !disabled && e.key === "Enter" && setEditing(true)}
        className={cn("text-sm rounded-xl p-3 min-h-[56px] transition-colors", !disabled && "cursor-text hover:bg-muted/30")}
      >
        {value.trim() ? (
          <Markdown text={value} className="text-foreground/90" />
        ) : (
          <span className="text-muted-foreground/60 italic">{t("tasks.task.addDescription", { defaultValue: "Add a description…" })}</span>
        )}
      </div>
    );
  }
  const tool = (icon: ReactNode, label: string, onClick: () => void) => (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      aria-label={label}
      title={label}
      className="w-7 h-7 rounded-md flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted"
    >
      {icon}
    </button>
  );
  return (
    <div className="border border-border rounded-xl focus-within:border-primary transition-colors">
      <div className="flex items-center gap-0.5 px-1.5 pt-1.5">
        {tool(<Bold className="size-3.5" />, t("tasks.task.bold", { defaultValue: "Bold" }), () => wrap("**"))}
        {tool(<Italic className="size-3.5" />, t("tasks.task.italic", { defaultValue: "Italics" }), () => wrap("_"))}
        {tool(<List className="size-3.5" />, t("tasks.task.bullets", { defaultValue: "Bullet list" }), bullet)}
        {tool(<Link2 className="size-3.5" />, t("tasks.task.link", { defaultValue: "Link" }), () => wrap("[", "](https://)", t("tasks.task.linkText", { defaultValue: "link" })))}
      </div>
      <textarea
        ref={ref}
        autoFocus
        rows={5}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            e.stopPropagation();
            setDraft(value);
            setEditing(false);
          }
        }}
        aria-label={t("tasks.hubs.wizard.description", { defaultValue: "Description" })}
        className="w-full text-sm resize-y bg-transparent p-3 outline-none leading-relaxed"
      />
    </div>
  );
}

// ── Labels (US 4.4) ───────────────────────────────────────────────────────

export function LabelsField({ labels, disabled, onChange }: { labels: Label[]; disabled?: boolean; onChange: (names: string[]) => void }) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState("");
  const [suggestions, setSuggestions] = useState<Label[]>([]);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    let alive = true;
    const handle = setTimeout(() => {
      void taskApi
        .labels(draft.trim())
        .then((r) => alive && setSuggestions(r))
        .catch(() => undefined);
    }, 150);
    return () => {
      alive = false;
      clearTimeout(handle);
    };
  }, [draft, open]);
  const names = labels.map((l) => l.name);
  const add = (name: string) => {
    const clean = name.trim();
    if (!clean) return;
    // Reuse the Workspace's spelling: "finance" picks "Finance" (US 4.4).
    const existing = suggestions.find((s) => s.name.toLowerCase() === clean.toLowerCase());
    const final = existing?.name ?? clean;
    if (!names.some((n) => n.toLowerCase() === final.toLowerCase())) onChange([...names, final]);
    setDraft("");
  };
  const shown = suggestions.filter((s) => !names.some((n) => n.toLowerCase() === s.name.toLowerCase()));
  return (
    <div className="flex items-center gap-1 flex-wrap">
      {labels.map((l) => (
        <span key={l.id} className="inline-flex items-center gap-1 h-6 ps-2 pe-1 rounded-md bg-primary/10 text-xs font-semibold text-foreground">
          {l.name}
          {!disabled && (
            <button
              type="button"
              onClick={() => onChange(names.filter((n) => n !== l.name))}
              aria-label={t("tasks.task.removeLabel", { defaultValue: "Remove {{name}}", name: l.name })}
              className="w-4 h-4 rounded flex items-center justify-center hover:bg-foreground/10"
            >
              <X className="size-3" />
            </button>
          )}
        </span>
      ))}
      {!disabled && (
        <span className="relative inline-flex">
          <input
            value={draft}
            onFocus={() => setOpen(true)}
            onChange={(e) => {
              setDraft(e.target.value);
              setOpen(true);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                add(draft);
              } else if (e.key === "Backspace" && !draft && names.length) {
                onChange(names.slice(0, -1));
              }
            }}
            aria-label={t("tasks.task.addLabel", { defaultValue: "Add label" })}
            placeholder={t("tasks.task.addLabel", { defaultValue: "Add label" })}
            className="w-28 h-6 px-2 bg-transparent text-xs outline-none border border-dashed border-border rounded-md focus:border-primary"
          />
          <Dropdown open={open && shown.length > 0} onClose={() => setOpen(false)} className="w-52 p-1 max-h-56 overflow-y-auto no-scrollbar">
            {shown.map((s) => (
              <button
                key={s.id}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => add(s.name)}
                className="w-full flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-muted rounded-lg text-start"
              >
                <Tag className="size-3.5 text-muted-foreground" />
                {s.name}
              </button>
            ))}
          </Dropdown>
        </span>
      )}
    </div>
  );
}

// ── Repeat (US 4.10) ──────────────────────────────────────────────────────

export function useRepeatLabel() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || "en";
  const dayName = (d: number) => new Date(2026, 10, 1 + d).toLocaleDateString(locale, { weekday: "short" });
  return (rule: RepeatRule | null): string => {
    if (!rule) return t("tasks.repeat.never", { defaultValue: "Doesn't repeat" });
    switch (rule.kind) {
      case "daily":
        return t("tasks.repeat.daily", { defaultValue: "Daily" });
      case "workday":
        return t("tasks.repeat.workday", { defaultValue: "Every workday" });
      case "weekly":
        return t("tasks.repeat.weeklyOn", { defaultValue: "Weekly on {{days}}", days: (rule.days ?? []).map(dayName).join(", ") });
      case "monthly":
        if (rule.mode === "pattern")
          return t("tasks.repeat.monthlyPattern", {
            defaultValue: "Monthly on the {{nth}} {{day}}",
            nth: nthWord(rule.nth ?? 1, t),
            day: new Date(2026, 10, 1 + (rule.weekday ?? 0)).toLocaleDateString(locale, { weekday: "long" }),
          });
        return t("tasks.repeat.monthlyDate", { defaultValue: "Monthly on day {{day}}", day: rule.day ?? "" });
      case "yearly":
        return t("tasks.repeat.yearly", { defaultValue: "Yearly" });
      case "custom":
        return t(`tasks.repeat.every_${rule.unit ?? "day"}`, { count: rule.interval ?? 1, defaultValue: `Every {{count}} ${rule.unit ?? "day"}s` });
      default:
        return "";
    }
  };
}

function nthWord(n: number, t: (k: string, o?: Record<string, unknown>) => string) {
  return {
    1: t("tasks.repeat.first", { defaultValue: "first" }),
    2: t("tasks.repeat.second", { defaultValue: "second" }),
    3: t("tasks.repeat.third", { defaultValue: "third" }),
    4: t("tasks.repeat.fourth", { defaultValue: "fourth" }),
    [-1]: t("tasks.repeat.last", { defaultValue: "last" }),
  }[n] as string;
}

export function RepeatField({
  rule,
  dueDate,
  disabled,
  onChange,
}: {
  rule: RepeatRule | null;
  dueDate: string | null;
  disabled?: boolean;
  onChange: (rule: RepeatRule | null) => void;
}) {
  const { t, i18n } = useTranslation();
  const label = useRepeatLabel();
  const workweek = useTasksSettings().data?.workweek ?? "sun_thu";
  const [open, setOpen] = useState(false);
  const [custom, setCustom] = useState(false);
  const base = dueDate ? parseIso(dueDate) : new Date();
  const weekday = base.getDay();
  const nth = Math.min(4, Math.ceil(base.getDate() / 7));
  const isLast = addDays(base, 7).getMonth() !== base.getMonth();
  const choices: [string, RepeatRule | null][] = [
    [t("tasks.repeat.never", { defaultValue: "Doesn't repeat" }), null],
    [label({ kind: "daily" }), { kind: "daily" }],
    [label({ kind: "workday" }), { kind: "workday" }],
    [label({ kind: "weekly", days: [weekday] }), { kind: "weekly", days: [weekday] }],
    [label({ kind: "monthly", mode: "date", day: base.getDate() }), { kind: "monthly", mode: "date", day: base.getDate() }],
    [label({ kind: "monthly", mode: "pattern", nth: isLast ? -1 : nth, weekday }), { kind: "monthly", mode: "pattern", nth: isLast ? -1 : nth, weekday }],
    [label({ kind: "yearly" }), { kind: "yearly" }],
  ];
  const preview = rule ? previewDates(rule, base, workweek) : [];
  return (
    <span className="relative inline-flex flex-col items-start">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        className={cn("flex items-center gap-1.5 text-sm px-2 py-0.5 rounded hover:bg-muted transition-colors font-medium disabled:hover:bg-transparent", rule ? "text-foreground" : "text-muted-foreground")}
      >
        <Repeat className="size-[13px]" />
        {label(rule)}
      </button>
      {rule && preview.length > 0 && (
        <span className="text-[11px] text-muted-foreground px-2" data-testid="repeat-preview">
          {t("tasks.repeat.next", { defaultValue: "Next: {{dates}}", dates: preview.map((d) => d.toLocaleDateString(i18n.language, { day: "numeric", month: "short" })).join(", ") })}
        </span>
      )}
      <Dropdown open={open} onClose={() => setOpen(false)} className="w-64 p-1">
        {choices.map(([text, value]) => (
          <button
            key={text}
            type="button"
            onClick={() => {
              onChange(value);
              setOpen(false);
            }}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-muted rounded-lg text-start"
          >
            <span className="flex-1">{text}</span>
            {JSON.stringify(value) === JSON.stringify(rule) && <Check className="size-3.5 text-primary" />}
          </button>
        ))}
        <div className="my-1 h-px bg-border" />
        <button
          type="button"
          onClick={() => {
            setOpen(false);
            setCustom(true);
          }}
          className="w-full flex items-center gap-2 px-3 py-2 text-sm font-semibold hover:bg-muted rounded-lg text-start"
        >
          {t("tasks.repeat.custom", { defaultValue: "Custom…" })}
        </button>
      </Dropdown>
      {custom && (
        <CustomRepeatDialog
          initial={rule}
          base={base}
          onClose={() => setCustom(false)}
          onSave={(r) => {
            onChange(r);
            setCustom(false);
          }}
        />
      )}
    </span>
  );
}

function CustomRepeatDialog({ initial, base, onClose, onSave }: { initial: RepeatRule | null; base: Date; onClose: () => void; onSave: (rule: RepeatRule) => void }) {
  const { t, i18n } = useTranslation();
  const workweek = useTasksSettings().data?.workweek ?? "sun_thu";
  const weekStart = useTasksSettings().data?.week_start ?? 0;
  const [mode, setMode] = useState<"interval" | "weekly">(initial?.kind === "weekly" ? "weekly" : "interval");
  const [interval, setInterval] = useState(initial?.kind === "custom" ? initial.interval ?? 1 : 2);
  const [unit, setUnit] = useState<"day" | "week" | "month">(initial?.kind === "custom" ? initial.unit ?? "day" : "week");
  const [days, setDays] = useState<number[]>(initial?.kind === "weekly" ? initial.days ?? [] : [base.getDay()]);
  const rule: RepeatRule = mode === "weekly" ? { kind: "weekly", days } : { kind: "custom", interval, unit };
  const valid = mode === "interval" ? interval >= 1 : days.length > 0;
  const preview = valid ? previewDates(rule, base, workweek) : [];
  const order = Array.from({ length: 7 }, (_, i) => (weekStart + i) % 7);
  return (
    <Modal onClose={onClose} labelledBy="custom-repeat-title" className="max-w-sm" z="z-[85]">
      <div className="px-6 pt-6 pb-4 flex items-start justify-between">
        <h2 id="custom-repeat-title" className="text-lg font-bold">
          {t("tasks.repeat.customTitle", { defaultValue: "Custom repeat" })}
        </h2>
        <CloseButton onClick={onClose} label={t("tasks.hubs.close", { defaultValue: "Close" })} />
      </div>
      <div className="px-6 pb-4 space-y-4">
        <div className="flex gap-1 p-1 bg-muted/40 rounded-xl" role="radiogroup">
          {(["interval", "weekly"] as const).map((m) => (
            <button
              key={m}
              type="button"
              role="radio"
              aria-checked={mode === m}
              onClick={() => setMode(m)}
              className={cn("flex-1 h-8 rounded-lg text-xs font-semibold", mode === m ? "bg-background shadow-sm" : "text-muted-foreground")}
            >
              {m === "interval" ? t("tasks.repeat.everyN", { defaultValue: "Every N days, weeks or months" }) : t("tasks.repeat.onDays", { defaultValue: "On days of the week" })}
            </button>
          ))}
        </div>
        {mode === "interval" ? (
          <div className="flex items-center gap-2 text-sm">
            <span>{t("tasks.repeat.every", { defaultValue: "Every" })}</span>
            <input
              type="number"
              min={1}
              max={365}
              value={interval}
              onChange={(e) => setInterval(Math.max(1, Number(e.target.value) || 1))}
              aria-label={t("tasks.repeat.interval", { defaultValue: "How often" })}
              className="w-16 h-9 px-2 border border-border rounded-lg bg-transparent outline-none focus:border-primary"
            />
            <select
              value={unit}
              onChange={(e) => setUnit(e.target.value as "day" | "week" | "month")}
              aria-label={t("tasks.repeat.unit", { defaultValue: "Unit" })}
              className="h-9 px-2 border border-border rounded-lg bg-transparent outline-none focus:border-primary"
            >
              <option value="day">{t("tasks.repeat.days", { defaultValue: "days" })}</option>
              <option value="week">{t("tasks.repeat.weeks", { defaultValue: "weeks" })}</option>
              <option value="month">{t("tasks.repeat.months", { defaultValue: "months" })}</option>
            </select>
          </div>
        ) : (
          <div className="flex gap-1">
            {order.map((d) => {
              const on = days.includes(d);
              return (
                <button
                  key={d}
                  type="button"
                  aria-pressed={on}
                  onClick={() => setDays((cur) => (on ? cur.filter((x) => x !== d) : [...cur, d]))}
                  className={cn("flex-1 h-9 rounded-lg text-xs font-semibold border", on ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted")}
                >
                  {new Date(2026, 10, 1 + d).toLocaleDateString(i18n.language, { weekday: "narrow" })}
                </button>
              );
            })}
          </div>
        )}
        {preview.length > 0 && (
          <p className="text-xs text-muted-foreground">
            {t("tasks.repeat.next", { defaultValue: "Next: {{dates}}", dates: preview.map((d) => d.toLocaleDateString(i18n.language, { day: "numeric", month: "short" })).join(", ") })}
          </p>
        )}
      </div>
      <div className="px-6 py-4 border-t border-border flex justify-end gap-2">
        <button type="button" onClick={onClose} className="h-10 px-4 rounded-xl border border-border text-sm font-semibold hover:bg-muted">
          {t("tasks.cancel", { defaultValue: "Cancel" })}
        </button>
        <button type="button" disabled={!valid} onClick={() => onSave(rule)} className="h-10 px-4 rounded-xl bg-foreground text-background text-sm font-bold disabled:opacity-40">
          {t("tasks.hubs.saveChanges", { defaultValue: "Save changes" })}
        </button>
      </div>
    </Modal>
  );
}

// ── Reminder (US 4.11) ────────────────────────────────────────────────────

export function ReminderField({
  reminder,
  dueDate,
  dueTime,
  timeZone,
  onSet,
  onClear,
  onSnooze,
}: {
  reminder: { remind_at: string; sent: boolean } | null;
  dueDate: string | null;
  dueTime: string | null;
  timeZone: string;
  onSet: (at: Date) => void;
  onClear: () => void;
  onSnooze: (body: { minutes: number } | { until: string }) => void;
}) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [custom, setCustom] = useState(false);
  const [customDate, setCustomDate] = useState<{ date: string | null; time: string | null }>({ date: null, time: "09:00" });
  const at = reminder ? new Date(reminder.remind_at) : null;
  const tomorrow9 = () => {
    const d = addDays(new Date(), 1);
    d.setHours(9, 0, 0, 0);
    return d;
  };
  const dayBeforeDue = () => {
    if (!dueDate) return null;
    const due = dueTime ? zonedInstant(dueDate, dueTime, timeZone) : (() => {
      const d = parseIso(dueDate);
      d.setHours(9, 0, 0, 0);
      return d;
    })();
    return new Date(due.getTime() - 86_400_000);
  };
  const shortcut = (label: string, value: Date | null) => (
    <button
      key={label}
      type="button"
      disabled={!value}
      onClick={() => {
        if (value) onSet(value);
        setOpen(false);
      }}
      className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-muted rounded-lg text-start disabled:opacity-40"
    >
      {label}
    </button>
  );
  const label = at
    ? `${at.toLocaleDateString(i18n.language, { weekday: "short", day: "numeric", month: "short" })}, ${at.toLocaleTimeString(i18n.language, { hour: "2-digit", minute: "2-digit" })}`
    : t("tasks.reminder.none", { defaultValue: "No reminder" });
  return (
    <span className="relative inline-flex flex-col items-start gap-1">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        className={cn("flex items-center gap-1.5 text-sm px-2 py-0.5 rounded hover:bg-muted transition-colors font-medium", at ? "text-foreground" : "text-muted-foreground")}
      >
        <Bell className="size-[13px]" />
        {label}
        {reminder?.sent && <span className="text-[11px] text-muted-foreground">· {t("tasks.reminder.sent", { defaultValue: "sent" })}</span>}
      </button>
      {reminder?.sent && (
        <span className="flex items-center gap-1 px-2" data-testid="reminder-snooze">
          <span className="text-[11px] text-muted-foreground">{t("tasks.reminder.snooze", { defaultValue: "Snooze" })}:</span>
          {[
            [t("tasks.reminder.snooze1h", { defaultValue: "1 hour" }), () => onSnooze({ minutes: 60 })],
            [t("tasks.reminder.snooze3h", { defaultValue: "3 hours" }), () => onSnooze({ minutes: 180 })],
            [t("tasks.reminder.snoozeTomorrow", { defaultValue: "Tomorrow" }), () => onSnooze({ until: tomorrow9().toISOString() })],
          ].map(([text, fn]) => (
            <button key={text as string} type="button" onClick={fn as () => void} className="h-6 px-2 rounded-md bg-muted/60 text-[11px] font-semibold hover:bg-muted">
              {text as string}
            </button>
          ))}
        </span>
      )}
      <Dropdown open={open} onClose={() => setOpen(false)} className="w-56 p-1">
        {shortcut(t("tasks.reminder.inOneHour", { defaultValue: "In 1 hour" }), new Date(Date.now() + 3_600_000))}
        {shortcut(t("tasks.reminder.tomorrow9", { defaultValue: "Tomorrow 9:00" }), tomorrow9())}
        {shortcut(t("tasks.reminder.dayBeforeDue", { defaultValue: "1 day before due" }), dayBeforeDue())}
        <button
          type="button"
          onClick={() => {
            setOpen(false);
            setCustom(true);
          }}
          className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-muted rounded-lg text-start"
        >
          {t("tasks.reminder.pick", { defaultValue: "Pick a date and time…" })}
        </button>
        {at && (
          <>
            <div className="my-1 h-px bg-border" />
            <button
              type="button"
              onClick={() => {
                onClear();
                setOpen(false);
              }}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-500 hover:bg-red-500/10 rounded-lg text-start"
            >
              <BellOff className="size-3.5" />
              {t("tasks.reminder.remove", { defaultValue: "Remove reminder" })}
            </button>
          </>
        )}
      </Dropdown>
      <Dropdown open={custom} onClose={() => setCustom(false)} className="p-0">
        <DatePickerPanel
          value={customDate}
          onChange={setCustomDate}
          onDone={() => {
            setCustom(false);
            if (customDate.date) {
              const d = parseIso(customDate.date);
              const [hh, mm] = (customDate.time || "09:00").split(":").map(Number);
              d.setHours(hh, mm, 0, 0);
              onSet(d);
            }
          }}
        />
      </Dropdown>
    </span>
  );
}

// ── Custom fields (US 4.14) ───────────────────────────────────────────────

export function CustomFieldInput({
  field,
  value,
  people,
  disabled,
  onChange,
}: {
  field: SquadField;
  value: unknown;
  people: { email: string; name: string }[];
  disabled?: boolean;
  onChange: (value: unknown) => void;
}) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(value == null ? "" : String(value));
  useEffect(() => setDraft(value == null ? "" : String(value)), [value]);
  const base = "h-8 px-2 bg-transparent border border-border rounded-lg text-sm outline-none focus:border-primary disabled:opacity-60 w-full max-w-60";
  const commitText = () => draft !== (value == null ? "" : String(value)) && onChange(draft === "" ? null : draft);
  const types: Record<FieldType, ReactNode> = {
    text: <input disabled={disabled} value={draft} onChange={(e) => setDraft(e.target.value)} onBlur={commitText} onKeyDown={(e) => e.key === "Enter" && (e.target as HTMLInputElement).blur()} aria-label={field.name} className={base} />,
    number: <input type="number" disabled={disabled} value={draft} onChange={(e) => setDraft(e.target.value)} onBlur={commitText} onKeyDown={(e) => e.key === "Enter" && (e.target as HTMLInputElement).blur()} aria-label={field.name} className={base} />,
    date: <input type="date" disabled={disabled} value={draft} onChange={(e) => onChange(e.target.value || null)} aria-label={field.name} className={base} />,
    dropdown: (
      <select disabled={disabled} value={draft} onChange={(e) => onChange(e.target.value || null)} aria-label={field.name} className={base}>
        <option value="">{t("tasks.fieldsUi.choose", { defaultValue: "Choose…" })}</option>
        {field.options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    ),
    checkbox: <input type="checkbox" disabled={disabled} checked={value === true} onChange={(e) => onChange(e.target.checked)} aria-label={field.name} className="size-4 accent-[var(--color-primary)]" />,
    person: (
      <select disabled={disabled} value={draft} onChange={(e) => onChange(e.target.value || null)} aria-label={field.name} className={base}>
        <option value="">{t("tasks.fieldsUi.nobody", { defaultValue: "Nobody" })}</option>
        {people.map((p) => (
          <option key={p.email} value={p.email}>
            {p.name}
          </option>
        ))}
      </select>
    ),
  };
  return <Fragment>{types[field.type]}</Fragment>;
}

export const todayIso = () => iso(new Date());
